import { useEffect, useRef, useCallback } from "react";
import { parseTripSnapshot } from "../services/storageService";
import type { City, TripLeg, StopActions } from "../data/types";

// ─── localStorage Trip Draft ─────────────────────────────────────────────
// Persists the current trip as a single draft in localStorage.
// Cloud saves happen only via explicit user action (bookmark button).

const STORAGE_KEY = "slow-travel-trip";
const DEBOUNCE_MS = 500;
export const CLOUD_TRIP_ID_KEY = "slow-travel-cloud-trip-id";

/** Set the cloud trip ID (used when loading a saved trip). */
export function setCloudTripId(id: string | null, owner?: string): void {
  try {
    if (id) {
      localStorage.setItem(CLOUD_TRIP_ID_KEY, id);
      if (owner) localStorage.setItem(`${CLOUD_TRIP_ID_KEY}-owner`, owner);
    } else {
      localStorage.removeItem(CLOUD_TRIP_ID_KEY);
      localStorage.removeItem(`${CLOUD_TRIP_ID_KEY}-owner`);
    }
  } catch { /* ignore */ }
}

export function getCloudTripOwner(): string | null {
  try { return localStorage.getItem(`${CLOUD_TRIP_ID_KEY}-owner`); } catch { return null; }
}

/** Get the current cloud trip ID. */
export function getCloudTripId(): string | null {
  try {
    return localStorage.getItem(CLOUD_TRIP_ID_KEY);
  } catch {
    return null;
  }
}

export interface PersistedTrip {
  tripStops: City[];
  stopActions: StopActions;
  tripName: string;
  travelDate: string;
  tripLegs: TripLeg[];
  savedAt: number;
}

/** Load persisted trip from localStorage, or null if none/invalid. */
export function loadPersistedTrip(): PersistedTrip | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedTrip>;
    const snapshot = parseTripSnapshot({ stops: parsed.tripStops, stopActions: parsed.stopActions,
      tripName: parsed.tripName, travelDate: parsed.travelDate || new Date().toISOString().slice(0, 10), legs: parsed.tripLegs });
    return { tripStops: snapshot.stops, stopActions: snapshot.stopActions, tripName: snapshot.tripName,
      travelDate: snapshot.travelDate, tripLegs: snapshot.legs, savedAt: parsed.savedAt || Date.now() };
  } catch {
    return null;
  }
}

/** Clear the local draft. Cloud identity is owned by useSavedTrips. */
function clearPersistedTrip(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* storage may be blocked */ }
}

/**
 * Hook that auto-saves trip state to localStorage only (single draft slot).
 * Cloud saves happen exclusively via the explicit save button in TripPanel.
 */
export function useTripPersistence(
  tripStops: City[],
  stopActions: StopActions,
  tripName: string,
  travelDate: string,
  tripLegs: TripLeg[] = []
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const save = useCallback(() => {
    try {
      const snapshot = parseTripSnapshot({ stops: tripStops, stopActions, tripName, travelDate, legs: tripLegs });
      const data: PersistedTrip = {
        tripStops: snapshot.stops,
        stopActions: snapshot.stopActions,
        tripName: snapshot.tripName,
        travelDate: snapshot.travelDate,
        tripLegs: snapshot.legs,
        savedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Invalid state or unavailable storage must not overwrite the last valid draft.
    }
  }, [tripStops, stopActions, tripName, travelDate, tripLegs]);

  useEffect(() => {
    // Empty trip → clear persisted data so refresh starts fresh
    if (tripStops.length === 0) {
      clearPersistedTrip();
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [save, tripStops.length]);
}
